/**
 * VerifiedSurfaceIndexPane — Read-only verified surface index
 *
 * Displays all verified governed workspace surfaces with their receipt
 * verification status and safety flags.
 *
 * SAFETY: Read-only inspection only. No execution authority, no deployment controls.
 */

import React from 'react';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface VerifiedSurfaceIndexPaneProps {
  model?: {
    // Optional model for future extensibility
  };
}

interface VerifiedSurface {
  surfaceId: string;
  stabilizationVerdict: string;
  receiptVerification: string;
  totalChecks: number;
  passedChecks: number;
  failedChecks: number;
  executionAuthority: boolean;
  proofOnly: boolean;
  replayable: boolean;
  immutableReference: boolean;
  rawPrivateContentExposed: boolean;
}

const VERIFIED_SURFACES: VerifiedSurface[] = [
  {
    surfaceId: 'release-state-map',
    stabilizationVerdict: 'STABILIZED',
    receiptVerification: 'PASSED',
    totalChecks: 5,
    passedChecks: 5,
    failedChecks: 0,
    executionAuthority: false,
    proofOnly: true,
    replayable: true,
    immutableReference: true,
    rawPrivateContentExposed: false,
  },
  {
    surfaceId: 'archive-cycle',
    stabilizationVerdict: 'STABILIZED',
    receiptVerification: 'PASSED',
    totalChecks: 5,
    passedChecks: 5,
    failedChecks: 0,
    executionAuthority: false,
    proofOnly: true,
    replayable: true,
    immutableReference: true,
    rawPrivateContentExposed: false,
  },
  {
    surfaceId: 'proposal-memory',
    stabilizationVerdict: 'STABILIZED',
    receiptVerification: 'PASSED',
    totalChecks: 13,
    passedChecks: 13,
    failedChecks: 0,
    executionAuthority: false,
    proofOnly: true,
    replayable: true,
    immutableReference: true,
    rawPrivateContentExposed: false,
  },
  {
    surfaceId: 'proposal-memory-release-link',
    stabilizationVerdict: 'STABILIZED',
    receiptVerification: 'PASSED',
    totalChecks: 16,
    passedChecks: 16,
    failedChecks: 0,
    executionAuthority: false,
    proofOnly: true,
    replayable: true,
    immutableReference: true,
    rawPrivateContentExposed: false,
  },
];

export const VerifiedSurfaceIndexPane: React.FC<VerifiedSurfaceIndexPaneProps> = ({ model }) => {
  const renderVerificationBadge = (status: string) => {
    const color = status === 'PASSED' ? SEMANTIC.stabilized : SEMANTIC.refusal;
    return (
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 600,
        color: color.text,
        backgroundColor: color.chip,
        padding: '2px 8px',
        borderRadius: 3,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        display: 'inline-block',
      }}>
        {status}
      </span>
    );
  };

  const renderSafetyFlag = (label: string, value: boolean) => {
    const color = value ? SEMANTIC.stabilized : SEMANTIC.refusal;
    return (
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: value ? color.text : SEMANTIC.refusal.text,
      }}>
        {label}: {String(value)}
      </span>
    );
  };

  const renderVerifiedSurface = (surface: VerifiedSurface, index: number) => (
    <div key={surface.surfaceId} style={{
      padding: SPACING.sp2,
      backgroundColor: NEUTRAL.bg600,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
      borderRadius: 2,
      marginBottom: SPACING.sp2,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: SPACING.sp1,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.caption.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
        }}>
          {surface.surfaceId}
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          backgroundColor: SEMANTIC.stabilized.chip,
          padding: '1px 4px',
          borderRadius: 2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          {surface.stabilizationVerdict}
        </span>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: SPACING.sp1,
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        marginBottom: SPACING.sp1,
      }}>
        <div>
          <span>• <strong>Total Checks:</strong> {surface.totalChecks}</span>
        </div>
        <div>
          <span>• <strong>Passed:</strong> {surface.passedChecks}</span>
        </div>
        <div>
          <span>• <strong>Failed:</strong> {surface.failedChecks}</span>
        </div>
        <div>
          <span>• <strong>Receipt Verification:</strong> {renderVerificationBadge(surface.receiptVerification)}</span>
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: SPACING.sp2,
        fontSize: TYPOGRAPHY.micro.size,
        paddingTop: SPACING.sp1,
        borderTop: `1px solid ${NEUTRAL.borderSubtle}`,
      }}>
        {renderSafetyFlag('executionAuthority', surface.executionAuthority)}
        {renderSafetyFlag('proofOnly', surface.proofOnly)}
        {renderSafetyFlag('replayable', surface.replayable)}
        {renderSafetyFlag('immutableReference', surface.immutableReference)}
        {renderSafetyFlag('rawPrivateContentExposed', surface.rawPrivateContentExposed)}
      </div>
    </div>
  );

  return (
    <div style={{
      padding: SPACING.sp3,
      height: '100%',
      overflowY: 'auto',
      backgroundColor: NEUTRAL.bg200,
    }}>
      {/* Header */}
      <div style={{
        fontSize: TYPOGRAPHY.label.size,
        fontWeight: 600,
        color: NEUTRAL.text500,
        marginBottom: SPACING.sp3,
        paddingBottom: SPACING.sp2,
        borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
      }}>
        VERIFIED SURFACE INDEX
      </div>

      {/* Safety Boundaries */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          SAFETY BOUNDARIES
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: SPACING.sp2,
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            READ-ONLY
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            VERIFIED SURFACES ONLY
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            NO EXECUTION AUTHORITY
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            NO MUTATION CONTROLS
          </span>
        </div>
      </div>

      {/* Verified Surfaces */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          VERIFIED SURFACES ({VERIFIED_SURFACES.length})
        </div>
        {VERIFIED_SURFACES.map((surface, index) => renderVerifiedSurface(surface, index))}
      </div>

      {/* Summary Stats */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          VERIFICATION SUMMARY
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
        }}>
          <div style={{
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Total Surfaces
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              {VERIFIED_SURFACES.length}
            </div>
          </div>
          <div style={{
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Total Checks
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              {VERIFIED_SURFACES.reduce((sum, s) => sum + s.totalChecks, 0)}
            </div>
          </div>
          <div style={{
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Passed Checks
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
            }}>
              {VERIFIED_SURFACES.reduce((sum, s) => sum + s.passedChecks, 0)}
            </div>
          </div>
          <div style={{
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Failed Checks
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: SEMANTIC.stabilized.text,
            }}>
              {VERIFIED_SURFACES.reduce((sum, s) => sum + s.failedChecks, 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Read-Only Notice */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
      }}>
        <strong>Read-only inspection only.</strong> This verified surface index displays receipt verification status and safety flags only. It does not import raw proposal content, deploy, rollback, mutate state, or grant execution authority.
      </div>
    </div>
  );
};
